import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Family, Invoice, FamilyBillingPreference } from '@/types/models';
import BillingStatusBadge from '../BillingStatusBadge';
import { useFamilyPayments } from '@/hooks/useFamilyPayments';
import FamilyPaymentHistory from '@/components/FamilyPaymentHistory';
import AdminFamilyInvoicesCard from './AdminFamilyInvoicesCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  ReceiptText, 
  CreditCard,
  BadgeCheck,
  BadgeAlert,
  Clock,
  Calendar,
  DollarSign,
  Pause,
  Play,
  X,
  Settings,
  Zap,
  FileText,
  ArrowRight,
  CheckCircle2,
  RefreshCw,
  Info,
  Mail,
  Copy
} from 'lucide-react';
import { BillingStatus } from '@/types/constants';
import { EmailService } from '@/services/emailService';
import { createInvoicePaymentSession } from '@/services/invoiceService';
import { getPrimaryParentEmail } from '@/services/familyServices/parentService';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from 'sonner';
import InvoiceDialog from '@/components/invoices/InvoiceDialog';
import { useInvoiceOperations } from '@/components/invoices/hooks/useInvoiceOperations';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BillingScheduleWithRelations, BillingScheduleStatus } from '@/types/billing';
import { format } from 'date-fns';
import { useStripeConnectStatus } from '@/hooks/useStripeConnectStatus';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Users, Shield, Link as LinkIcon } from 'lucide-react';

interface FamilyBillingTabProps {
  family: Family;
  onUpdateBillingStatus?: (status: BillingStatus) => Promise<void>;
}

const FamilyBillingTab: React.FC<FamilyBillingTabProps> = ({ 
  family,
  onUpdateBillingStatus
}) => {
  const [activeSubtab, setActiveSubtab] = useState('payments');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [families, setFamilies] = useState<Family[]>([]);
  const [refreshInvoices, setRefreshInvoices] = useState<(() => void) | null>(null);
  const [updatingPreference, setUpdatingPreference] = useState(false);
  const [checkingCustomerStatus, setCheckingCustomerStatus] = useState(false);
  const [sendingSetupEmail, setSendingSetupEmail] = useState(false);
  const [customerStatus, setCustomerStatus] = useState<{
    hasCustomer: boolean;
    hasPaymentMethod: boolean;
    customerEmail?: string;
    lastChecked?: Date;
  } | null>(null);
  const { organizationId } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  
  // Check Stripe Connect status
  const { data: stripeStatus } = useStripeConnectStatus();

  // Get primary parent email for customer operations
  const { data: primaryParentEmail } = useQuery({
    queryKey: ['primary-parent-email', family.id],
    queryFn: () => getPrimaryParentEmail(family.id),
    enabled: !!family.id,
  });

  // Get billing schedules for this family
  const { data: familyBillingSchedules = [], isLoading: schedulesLoading } = useQuery<BillingScheduleWithRelations[]>({
    queryKey: ['family-billing-schedules', family.id],
    queryFn: async () => {
      if (!organizationId) return [];
      
      // Get all students in this family
      const { data: students } = await supabase
        .from('students')
        .select('id')
        .eq('family_id', family.id);

      if (!students || students.length === 0) return [];

      const studentIds = students.map(s => s.id);
      
      // Get billing schedules for these students
      const { data: schedules } = await supabase
        .from('billing_schedules')
        .select(`
          id,
          enrollment_id,
          organization_id,
          amount_cents,
          billing_day,
          next_billing_date,
          status,
          description,
          created_at,
          enrollment:enrollments!inner(
            id,
            student_id,
            class_id,
            organization_id,
            student:students!enrollments_student_id_fkey(id, first_name, last_name, family_id),
            class:classes!enrollments_class_id_fkey(id, name, price_cents)
          )
        `)
        .in('enrollment.student_id', studentIds)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      return schedules as BillingScheduleWithRelations[] || [];
    },
    enabled: !!organizationId && !!family.id,
  });

  // Get family enrollments for billing setup
  const { data: familyEnrollments = [] } = useQuery({
    queryKey: ['family-enrollments', family.id],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data: students } = await supabase
        .from('students')
        .select('id')
        .eq('family_id', family.id);

      if (!students || students.length === 0) return [];

      const studentIds = students.map(s => s.id);
      
      const { data: enrollments } = await supabase
        .from('enrollments')
        .select(`
          id,
          student_id,
          class_id,
          status,
          student:students!enrollments_student_id_fkey(id, first_name, last_name),
          class:classes!enrollments_class_id_fkey(id, name, price_cents)
        `)
        .in('student_id', studentIds)
        .eq('status', 'active')
        .eq('organization_id', organizationId);

      return enrollments || [];
    },
    enabled: !!organizationId && !!family.id,
  });

  // Use the existing invoice operations hook
  const {
    isAddInvoiceDialogOpen,
    setIsAddInvoiceDialogOpen,
    handleAddInvoice,
    handleCreateInvoice
  } = useInvoiceOperations(organizationId);

  // Load families for the invoice dialog
  useEffect(() => {
    const loadFamilies = async () => {
      if (!organizationId) return;
      
      try {
        const { data, error } = await supabase
          .from('families')
          .select('*')
          .eq('organization_id', organizationId);
        
        if (error) throw error;
        setFamilies(data || []);
      } catch (error) {
        console.error('Error loading families:', error);
      }
    };

    loadFamilies();
  }, [organizationId]);

  const handleStatusChange = async (status: BillingStatus) => {
    if (!onUpdateBillingStatus) return;
    
    try {
      setUpdatingStatus(true);
      await onUpdateBillingStatus(status);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendInvoice = async (invoice: Invoice) => {
    try {
      // Get the primary parent's email for this family
      const familyEmail = await getPrimaryParentEmail(invoice.family_id);
      
      if (!familyEmail) {
        toast.error('No primary parent email found for this family');
        return;
      }

      const organizationName = organization?.name || 'Your Activity Center';

      let paymentUrl: string | undefined;

      // Try to generate payment link, but continue even if it fails
      try {
        const paymentSession = await createInvoicePaymentSession({
          invoiceId: invoice.id,
          familyId: invoice.family_id,
          organizationId: invoice.organization_id,
          amount_cents: invoice.total_cents,
          customer_email: familyEmail,
          success_url: window.location.origin + '/payment-success',
          cancel_url: window.location.origin + '/payment-cancelled',
        });
        
        paymentUrl = paymentSession?.url;
      } catch (paymentError) {
        console.warn('Payment session creation failed, continuing without payment link:', paymentError);
        // Don't throw - we'll send the email without a payment link
      }

      // Send the email (with or without payment link)
      const result = await EmailService.sendInvoiceEmail(
        invoice,
        familyEmail,
        organizationName,
        paymentUrl,
        organization?.logo_url
      );

      if (result.success) {
        if (paymentUrl) {
          toast.success('Invoice sent successfully with payment link!');
        } else {
          toast.success('Invoice sent successfully! Online payment not available - family should contact you for payment options.');
        }
      } else {
        toast.error(`Failed to send invoice: ${result.error}`);
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  const handleCreateInvoiceForFamily = () => {
    // Open the invoice creation dialog
    handleAddInvoice();
  };

  // Custom invoice creation handler that refreshes the list
  const handleCreateInvoiceWithRefresh = async (data: unknown) => {
    const result = await handleCreateInvoice(data);
    if (result && refreshInvoices) {
      // Refresh the invoices list after successful creation
      refreshInvoices();
    }
    return result;
  };

  // Callback to capture the refresh function from AdminFamilyInvoicesCard
  const handleInvoiceListReady = (refreshFn: () => void) => {
    setRefreshInvoices(() => refreshFn);
  };

  // Handle billing schedule status changes
  const handleScheduleStatusChange = async (scheduleId: string, newStatus: BillingScheduleStatus) => {
    try {
      const { error } = await supabase
        .from('billing_schedules')
        .update({ status: newStatus })
        .eq('id', scheduleId);

      if (error) throw error;

      toast.success(`Billing schedule ${newStatus === 'active' ? 'activated' : newStatus === 'paused' ? 'paused' : 'cancelled'}`);
      queryClient.invalidateQueries({ queryKey: ['family-billing-schedules', family.id] });
    } catch (error: unknown) {
      toast.error('Failed to update billing schedule');
      console.error(error);
    }
  };

  // Calculate family totals
  const activeBillingSchedules = familyBillingSchedules.filter(s => s.status === 'active');
  const totalMonthlyAmount = activeBillingSchedules.reduce((sum, s) => sum + s.amount_cents, 0);
  
  // Calculate next charge date
  const nextChargeDate = activeBillingSchedules.length > 0 
    ? activeBillingSchedules
        .map(s => new Date(s.next_billing_date))
        .sort((a, b) => a.getTime() - b.getTime())[0]
    : null;

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  // Handle family billing preference updates
  const handleBillingPreferenceChange = async (preference: FamilyBillingPreference) => {
    if (!organizationId) return;
    
    setUpdatingPreference(true);
    try {
      const { error } = await supabase
        .from('families')
        .update({ billing_preference: preference })
        .eq('id', family.id)
        .eq('organization_id', organizationId);

      if (error) throw error;

      const preferenceLabel = preference === 'auto' ? 'Auto-Billing' : preference === 'manual' ? 'Manual Billing' : 'Default';
      toast.success(`Family billing preference set to ${preferenceLabel}`);
      
      // Refresh the family data - we'd need to add this to the parent component
      // For now, we'll just show the success message
    } catch (error) {
      console.error('Error updating billing preference:', error);
      toast.error('Failed to update billing preference');
    } finally {
      setUpdatingPreference(false);
    }
  };

  // Check customer and payment method status
  const checkCustomerStatus = async () => {
    if (!organizationId || !family.id || !primaryParentEmail) {
      return;
    }

    setCheckingCustomerStatus(true);
    try {
      // Check if customer exists in our system
      const { data: organization } = await supabase
        .from('organizations')
        .select('billing_settings')
        .eq('id', organizationId)
        .single();

      const stripeAccountId = organization?.billing_settings?.stripe_account_id || null;

      const { data: existingCustomer } = await supabase
        .from('stripe_customers')
        .select('stripe_customer_id, default_payment_method, status')
        .eq('family_id', family.id)
        .eq('organization_id', organizationId)
        .eq('stripe_account_id', stripeAccountId || 'platform')
        .eq('status', 'active')
        .single();

      const hasCustomer = !!existingCustomer;
      const hasPaymentMethod = hasCustomer && !!existingCustomer.default_payment_method;

      setCustomerStatus({
        hasCustomer,
        hasPaymentMethod,
        customerEmail: primaryParentEmail,
        lastChecked: new Date()
      });
    } catch (error) {
      console.error('Error checking customer status:', error);
      setCustomerStatus({
        hasCustomer: false,
        hasPaymentMethod: false,
        customerEmail: primaryParentEmail,
        lastChecked: new Date()
      });
    } finally {
      setCheckingCustomerStatus(false);
    }
  };

  // Auto-check customer status when needed
  useEffect(() => {
    if (stripeStatus?.isActive && primaryParentEmail && !customerStatus) {
      checkCustomerStatus();
    }
  }, [stripeStatus?.isActive, primaryParentEmail, family.id, organizationId]);

  // Send setup email to family
  const handleSendSetupEmail = async () => {
    if (!primaryParentEmail || !organizationId) {
      toast.error('Primary parent email not found');
      return;
    }

    setSendingSetupEmail(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-setup-email', {
        body: {
          familyId: family.id,
          organizationId,
          email: primaryParentEmail,
          familyName: family.name,
          organizationName: organization?.name || 'Your Organization'
        }
      });

      if (error) throw error;

      toast.success(`Setup email sent to ${primaryParentEmail}`);
    } catch (error) {
      console.error('Error sending setup email:', error);
      toast.error('Failed to send setup email');
    } finally {
      setSendingSetupEmail(false);
    }
  };

  // Generate and copy payment setup link
  const handleCopySetupLink = async () => {
    if (!primaryParentEmail || !organizationId) {
      toast.error('Primary parent email not found');
      return;
    }

    try {
      // Create a customer session for payment method setup
      const { data, error } = await supabase.functions.invoke('create-customer-portal-session', {
        body: {
          familyId: family.id,
          organizationId,
          email: primaryParentEmail,
          return_url: `${window.location.origin}/parent`
        }
      });

      if (error) throw error;

      if (data?.url) {
        await navigator.clipboard.writeText(data.url);
        toast.success('Payment setup link copied to clipboard!');
      } else {
        // Fallback: create a generic setup link
        const setupLink = `${window.location.origin}/parent/billing-setup?family=${family.id}&email=${encodeURIComponent(primaryParentEmail)}`;
        await navigator.clipboard.writeText(setupLink);
        toast.success('Setup link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error creating setup link:', error);
      // Fallback: create a generic setup link
      try {
        const setupLink = `${window.location.origin}/parent/billing-setup?family=${family.id}&email=${encodeURIComponent(primaryParentEmail)}`;
        await navigator.clipboard.writeText(setupLink);
        toast.success('Setup link copied to clipboard!');
      } catch (clipboardError) {
        toast.error('Failed to copy setup link');
      }
    }
  };

  // Helper to determine if enrollment can have auto-billing
  const canEnableAutoBilling = (enrollment: unknown) => {
    // Check if already has a billing schedule
    const hasSchedule = familyBillingSchedules.some(
      schedule => schedule.enrollment_id === enrollment.id
    );
    return !hasSchedule && stripeStatus?.isActive;
  };

  // Enhanced auto-billing setup with customer creation
  const setupAutoBillingWithCustomer = async (enrollment: any) => {
    if (!organizationId || !primaryParentEmail) {
      toast.error('Missing required information for auto-billing setup');
      return;
    }

    try {
      // First ensure customer exists
      const { data: customerResult, error: customerError } = await supabase.functions.invoke('create-family-stripe-customer', {
        body: {
          familyId: family.id,
          organizationId,
          customerEmail: primaryParentEmail,
          customerName: family.name
        }
      });

      if (customerError) {
        console.error('Customer creation failed:', customerError);
        toast.error('Failed to create customer record. Please try again.');
        return;
      }

      console.log('Customer setup result:', customerResult);
      
      // Refresh customer status
      await checkCustomerStatus();
      
      // Now enable auto-billing
      await handleEnableAutoBilling(enrollment);
      
      toast.success(`Auto-billing setup initiated for ${enrollment.student.first_name}! They'll need to add a payment method to complete setup.`);
    } catch (error: any) {
      console.error('Error in auto-billing setup:', error);
      toast.error('Failed to setup auto-billing. Please try again.');
    }
  };

  // Get enrollments without billing schedules
  const enrollmentsWithoutBilling = familyEnrollments.filter(enrollment =>
    !familyBillingSchedules.some(schedule => schedule.enrollment_id === enrollment.id)
  );

  // Handle enabling auto-billing for an enrollment
  const handleEnableAutoBilling = async (enrollment: any) => {
    if (!organizationId) {
      console.error('No organization ID available');
      toast.error('Organization ID is required');
      return;
    }
    
    try {
      console.log('Enabling auto-billing for enrollment:', enrollment);
      
      // Validate required fields
      if (!enrollment?.id) {
        throw new Error('Enrollment ID is missing');
      }
      if (!enrollment?.class?.price_cents) {
        throw new Error('Class price is missing');
      }
      if (!enrollment?.class?.name) {
        throw new Error('Class name is missing');
      }
      if (!enrollment?.student?.first_name) {
        throw new Error('Student name is missing');
      }

      // Calculate next billing date properly
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const nextBillingDate = nextMonth.toISOString().split('T')[0]; // YYYY-MM-DD format

      console.log('Calculated next billing date:', nextBillingDate);

      // Create billing schedule for auto-billing (status: active)
      const billingScheduleData = {
        enrollment_id: enrollment.id,
        organization_id: organizationId,
        amount_cents: enrollment.class.price_cents,
        billing_day: 1, // Default to 1st of the month
        status: 'active' as const,
        description: `Auto-billing for ${enrollment.class.name}`,
        next_billing_date: nextBillingDate
      };

      console.log('Inserting auto-billing schedule data:', billingScheduleData);

      const { data, error } = await supabase
        .from('billing_schedules')
        .insert(billingScheduleData)
        .select()
        .single();

      if (error) {
        console.error('Database error enabling auto-billing:', error);
        throw new Error(`Database error: ${error.message} (Code: ${error.code})`);
      }

      console.log('Successfully enabled auto-billing:', data);

      toast.success(`Auto-billing enabled for ${enrollment.student.first_name} in ${enrollment.class.name}`);
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['family-billing-schedules', family.id] });
      queryClient.invalidateQueries({ queryKey: ['billing-schedules', organizationId] });
    } catch (error: any) {
      console.error('Error enabling auto-billing:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      toast.error(`Failed to enable auto-billing: ${errorMessage}`);
    }
  };

  // Handle bulk billing setup for all enrollments
  const handleBulkBillingSetup = async (scope: 'all') => {
    if (!organizationId) {
      toast.error('Organization ID is required');
      return;
    }

    try {
      const billingType = family.billing_preference === 'auto' && stripeStatus?.isActive ? 'auto' : 'manual';
      const enrollments = enrollmentsWithoutBilling;
      
      if (enrollments.length === 0) {
        toast.info('No enrollments need billing setup');
        return;
      }

      // Create billing schedules for all enrollments
      for (const enrollment of enrollments) {
        if (billingType === 'auto') {
          await handleEnableAutoBilling(enrollment);
        } else {
          await handleSetBillingSchedule(enrollment);
        }
      }

      toast.success(`Billing setup completed for ${enrollments.length} enrollment${enrollments.length !== 1 ? 's' : ''}`);
    } catch (error: any) {
      console.error('Error in bulk billing setup:', error);
      toast.error('Failed to complete bulk billing setup');
    }
  };

  // Handle bulk billing setup for a specific student's enrollments
  const handleBulkStudentBilling = async (enrollments: any[], billingType: 'auto' | 'manual') => {
    if (!organizationId) {
      toast.error('Organization ID is required');
      return;
    }

    try {
      if (enrollments.length === 0) {
        toast.info('No enrollments to process');
        return;
      }

      const studentName = `${enrollments[0].student.first_name} ${enrollments[0].student.last_name}`;
      
      // Create billing schedules for all student's enrollments
      const promises = enrollments.map(enrollment => {
        if (billingType === 'auto') {
          return handleEnableAutoBilling(enrollment);
        } else {
          return handleSetBillingSchedule(enrollment);
        }
      });

      await Promise.all(promises);

      const action = billingType === 'auto' ? 'Auto-billing enabled' : 'Billing schedules created';
      toast.success(`${action} for all ${studentName}'s classes (${enrollments.length} ${enrollments.length === 1 ? 'class' : 'classes'})`);
    } catch (error: any) {
      console.error('Error in bulk student billing setup:', error);
      const studentName = enrollments.length > 0 ? `${enrollments[0].student.first_name} ${enrollments[0].student.last_name}` : 'student';
      toast.error(`Failed to setup billing for ${studentName}'s classes`);
    }
  };

  // Handle setting up manual billing schedule for an enrollment
  const handleSetBillingSchedule = async (enrollment: any) => {
    if (!organizationId) {
      console.error('No organization ID available');
      toast.error('Organization ID is required');
      return;
    }
    
    try {
      console.log('Creating billing schedule for enrollment:', enrollment);
      
      // Validate required fields
      if (!enrollment?.id) {
        throw new Error('Enrollment ID is missing');
      }
      if (!enrollment?.class?.price_cents) {
        throw new Error('Class price is missing');
      }
      if (!enrollment?.class?.name) {
        throw new Error('Class name is missing');
      }
      if (!enrollment?.student?.first_name) {
        throw new Error('Student name is missing');
      }

      // Calculate next billing date properly
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const nextBillingDate = nextMonth.toISOString().split('T')[0]; // YYYY-MM-DD format

      console.log('Calculated next billing date:', nextBillingDate);

      // Create billing schedule for manual billing (status: paused)
      const billingScheduleData = {
        enrollment_id: enrollment.id,
        organization_id: organizationId,
        amount_cents: enrollment.class.price_cents,
        billing_day: 1, // Default to 1st of the month
        status: 'paused' as const, // Manual billing starts paused
        description: `Manual billing schedule for ${enrollment.class.name}`,
        next_billing_date: nextBillingDate
      };

      console.log('Inserting billing schedule data:', billingScheduleData);

      const { data, error } = await supabase
        .from('billing_schedules')
        .insert(billingScheduleData)
        .select()
        .single();

      if (error) {
        console.error('Database error creating billing schedule:', error);
        throw new Error(`Database error: ${error.message} (Code: ${error.code})`);
      }

      console.log('Successfully created billing schedule:', data);

      toast.success(`Billing schedule created for ${enrollment.student.first_name} in ${enrollment.class.name}`);
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['family-billing-schedules', family.id] });
      queryClient.invalidateQueries({ queryKey: ['billing-schedules', organizationId] });
    } catch (error: any) {
      console.error('Error creating billing schedule:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      toast.error(`Failed to create billing schedule: ${errorMessage}`);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Account Status</CardTitle>
          <BillingStatusBadge status={family.billing_status || 'inactive'} />
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mt-2">
            <Button 
              variant={family.billing_status === 'current' ? 'default' : 'outline'} 
              size="sm"
              disabled={updatingStatus || family.billing_status === 'current'}
              onClick={() => handleStatusChange('current')}
              className={family.billing_status === 'current' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              <BadgeCheck className="mr-1 h-4 w-4" />
              Mark as Paid
            </Button>
            <Button 
              variant={family.billing_status === 'overdue' ? 'default' : 'outline'} 
              size="sm" 
              className={family.billing_status === 'overdue' ? 'bg-red-600 hover:bg-red-700' : ''}
              disabled={updatingStatus || family.billing_status === 'overdue'}
              onClick={() => handleStatusChange('overdue')}
            >
              <BadgeAlert className="mr-1 h-4 w-4" />
              Flag as Due
            </Button>
            <Button 
              variant={family.billing_status === 'inactive' ? 'default' : 'outline'} 
              size="sm"
              disabled={updatingStatus || family.billing_status === 'inactive'}
              onClick={() => handleStatusChange('inactive')}
              className={family.billing_status === 'inactive' ? 'bg-gray-500 hover:bg-gray-600' : ''}
            >
              <Clock className="mr-1 h-4 w-4" />
              Pause Billing
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <Tabs value={activeSubtab} onValueChange={setActiveSubtab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="payments" className="flex items-center gap-1">
            <CreditCard className="h-4 w-4" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex items-center gap-1">
            <ReceiptText className="h-4 w-4" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="auto-billing" className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            Auto-Billing
            {activeBillingSchedules.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {activeBillingSchedules.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="payments">
          <FamilyPaymentHistory familyId={family.id} />
        </TabsContent>
        
        <TabsContent value="invoices">
          <AdminFamilyInvoicesCard
            familyId={family.id}
            onSendInvoice={handleSendInvoice}
            onCreateInvoice={handleCreateInvoiceForFamily}
            onInvoiceListReady={handleInvoiceListReady}
          />
        </TabsContent>

        <TabsContent value="auto-billing">
          <div className="space-y-6">
            {/* Billing Preference Section */}
            <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5 text-blue-600" />
                    Billing Preference
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Auto-Billing Option */}
                    <div className={`p-4 rounded-lg border-2 transition-all ${
                      !stripeStatus?.isActive 
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' 
                        : family.billing_preference === 'auto' 
                          ? 'border-green-500 bg-green-50 cursor-pointer' 
                          : 'border-gray-200 hover:border-green-300 cursor-pointer'
                    }`}
                    onClick={() => stripeStatus?.isActive && handleBillingPreferenceChange('auto')}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Zap className="h-5 w-5 text-green-600" />
                          <span className="font-semibold text-green-700">Auto-Billing</span>
                        </div>
                        {family.billing_preference === 'auto' && (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {stripeStatus?.isActive 
                          ? "Automatic monthly charges via Stripe. Convenient and hands-off."
                          : "Requires Stripe Connect setup. Contact admin to enable auto-billing."
                        }
                      </p>
                      <div className="flex items-center gap-2 text-xs text-green-600">
                        {stripeStatus?.isActive ? (
                          <>
                            <span>• Automatic processing</span>
                            <span>• Failed payment retries</span>
                            <span>• Email receipts</span>
                          </>
                        ) : (
                          <span className="text-gray-500">• Setup required</span>
                        )}
                      </div>
                    </div>

                    {/* Manual Billing Option */}
                    <div className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      family.billing_preference === 'manual' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => handleBillingPreferenceChange('manual')}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <span className="font-semibold text-blue-700">Manual Billing</span>
                        </div>
                        {family.billing_preference === 'manual' && (
                          <CheckCircle2 className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Traditional invoices sent manually. More control over timing.
                      </p>
                      <div className="flex items-center gap-2 text-xs text-blue-600">
                        <span>• Manual control</span>
                        <span>• Flexible timing</span>
                        <span>• Custom invoices</span>
                      </div>
                    </div>
                  </div>

                  <Alert className="mt-4 border-blue-200 bg-blue-50">
                    <Settings className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      You can change this billing preference at any time. New families default to manual billing.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

            {/* Enhanced Billing Summary with Detailed Breakdown */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    Billing Summary
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={
                      family.billing_preference === 'auto' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }>
                      {family.billing_preference === 'auto' ? 'Auto-Billing' : 'Manual Billing'}
                    </Badge>
                    {stripeStatus?.isActive && (
                      <Badge className="bg-green-100 text-green-800">
                        Stripe Connected
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-700">
                        {activeBillingSchedules.length}
                      </div>
                      <div className="text-sm text-blue-600">Active Schedules</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-700">
                        ${(totalMonthlyAmount / 100).toFixed(2)}
                      </div>
                      <div className="text-sm text-green-600">Monthly Total</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-lg font-bold text-orange-700">
                        {nextChargeDate ? format(nextChargeDate, 'MMM d') : 'None'}
                      </div>
                      <div className="text-sm text-orange-600">Next Charge</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-700">
                        {familyBillingSchedules.length}
                      </div>
                      <div className="text-sm text-purple-600">Total Schedules</div>
                    </div>
                  </div>

                  {/* Detailed Charge Breakdown Preview */}
                  {activeBillingSchedules.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Charge Breakdown Preview</h4>
                        <div className="text-sm text-gray-600">
                          Next billing: {nextChargeDate ? format(nextChargeDate, 'MMMM d, yyyy') : 'Not scheduled'}
                        </div>
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b">
                          <div className="grid grid-cols-4 gap-4 text-sm font-medium text-gray-700">
                            <div>Student</div>
                            <div>Class</div>
                            <div>Amount</div>
                            <div>Status</div>
                          </div>
                        </div>
                        <div className="divide-y">
                          {activeBillingSchedules.map((schedule, index) => (
                            <div key={schedule.id} className={`px-4 py-3 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              <div className="grid grid-cols-4 gap-4 items-center">
                                <div className="font-medium text-gray-900">
                                  {schedule.enrollment.student.first_name} {schedule.enrollment.student.last_name}
                                </div>
                                <div className="text-gray-700">
                                  {schedule.enrollment.class.name}
                                </div>
                                <div className="font-semibold text-green-700">
                                  ${(schedule.amount_cents / 100).toFixed(2)}
                                </div>
                                <div>
                                  <Badge className={getStatusBadge(schedule.status)}>
                                    {schedule.status}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="bg-green-50 px-4 py-3 border-t border-green-200">
                          <div className="flex justify-between items-center">
                            <div className="font-medium text-green-800">Total Monthly Charge</div>
                            <div className="text-xl font-bold text-green-800">
                              ${(totalMonthlyAmount / 100).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Enhanced Auto-billing Setup Status */}
            {stripeStatus?.isActive ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Shield className="h-5 w-5 text-green-600" />
                    Auto-Billing Setup Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Customer Status */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${customerStatus?.hasCustomer ? 'bg-green-500' : 'bg-yellow-500'}`} />
                        <div>
                          <div className="font-medium">Customer Record</div>
                          <div className="text-sm text-gray-600">
                            {customerStatus?.hasCustomer 
                              ? 'Family is set up in payment system'
                              : 'Customer record will be created automatically'
                            }
                          </div>
                        </div>
                      </div>
                      {customerStatus?.hasCustomer && (
                        <Badge className="bg-green-100 text-green-800">Ready</Badge>
                      )}
                    </div>

                    {/* Payment Method Status */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${customerStatus?.hasPaymentMethod ? 'bg-green-500' : 'bg-amber-500'}`} />
                        <div>
                          <div className="font-medium">Payment Method</div>
                          <div className="text-sm text-gray-600">
                            {customerStatus?.hasPaymentMethod 
                              ? 'Family has payment method on file'
                              : 'Family will need to add a payment method'
                            }
                          </div>
                        </div>
                      </div>
                      {customerStatus?.hasPaymentMethod ? (
                        <Badge className="bg-green-100 text-green-800">Ready</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                      )}
                    </div>

                    {/* Setup Actions */}
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={checkCustomerStatus}
                          disabled={checkingCustomerStatus}
                          className="flex items-center gap-2"
                        >
                          {checkingCustomerStatus ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          Refresh Status
                        </Button>
                        
                        {primaryParentEmail && (
                          <div className="text-sm text-gray-600 flex items-center gap-2">
                            <span>Primary email:</span>
                            <code className="px-2 py-1 bg-gray-100 rounded text-xs">{primaryParentEmail}</code>
                          </div>
                        )}
                      </div>

                      {/* Actionable Setup Buttons */}
                      {primaryParentEmail && !customerStatus?.hasPaymentMethod && (
                        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium text-amber-800">Payment Setup Required</div>
                            <div className="text-sm text-amber-700">
                              Family needs to add payment method to complete auto-billing setup
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleSendSetupEmail}
                              disabled={sendingSetupEmail}
                              className="border-amber-300 text-amber-700 hover:bg-amber-100"
                            >
                              {sendingSetupEmail ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : (
                                <Mail className="h-4 w-4 mr-2" />
                              )}
                              Send Email
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleCopySetupLink}
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Link
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Enhanced Helpful Info */}
                    <Alert className="border-blue-200 bg-blue-50">
                      <Info className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        <strong>How auto-billing works:</strong> When you enable auto-billing, we'll create a customer record and guide the family through payment method setup. Use the "Send Email" button to notify families when setup is needed, or "Copy Link" to share the setup URL directly.
                      </AlertDescription>
                    </Alert>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Alert className="border-yellow-200 bg-yellow-50">
                <Clock className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  <strong>Auto-billing not available.</strong> {
                    stripeStatus?.status === 'not_connected' 
                      ? 'Stripe Connect is not set up for this organization.'
                      : stripeStatus?.status === 'pending'
                      ? 'Stripe Connect setup is pending verification.'
                      : stripeStatus?.status === 'error'
                      ? 'Stripe Connect has configuration issues.'
                      : 'Stripe Connect is not properly configured.'
                  } Only manual billing is available.
                </AlertDescription>
              </Alert>
            )}

            {/* Elegant Sibling Billing Setup */}
            {enrollmentsWithoutBilling.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Setup Billing</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Configure billing for each child's classes</p>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {enrollmentsWithoutBilling.length} classes need setup
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Group enrollments by student */}
                    {Object.entries(
                      enrollmentsWithoutBilling.reduce((acc: any, enrollment: any) => {
                        const studentKey = enrollment.student.id;
                        if (!acc[studentKey]) {
                          acc[studentKey] = {
                            student: enrollment.student,
                            enrollments: []
                          };
                        }
                        acc[studentKey].enrollments.push(enrollment);
                        return acc;
                      }, {})
                    ).map(([studentKey, studentData]: [string, any]) => {
                      const totalAmount = studentData.enrollments.reduce((sum: number, e: any) => sum + e.class.price_cents, 0);
                      return (
                        <div key={studentKey} className="border border-gray-200 rounded-xl p-5 bg-gradient-to-r from-gray-50 to-white">
                          {/* Student Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                                <span className="text-white font-bold text-lg">
                                  {studentData.student.first_name[0]}
                                </span>
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900 text-lg">
                                  {studentData.student.first_name} {studentData.student.last_name}
                                </h3>
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                  <span>{studentData.enrollments.length} class{studentData.enrollments.length !== 1 ? 'es' : ''}</span>
                                  <span className="font-medium text-green-700">${(totalAmount / 100).toFixed(2)}/month</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Enhanced Individual Controls */}
                            <div className="flex items-center gap-3">
                              {family.billing_preference === 'auto' && stripeStatus?.isActive ? (
                                <div className="space-y-2">
                                  {/* Customer Status Indicator */}
                                  {checkingCustomerStatus ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      <span>Checking setup status...</span>
                                    </div>
                                  ) : customerStatus ? (
                                    <div className="flex items-center gap-2 text-xs">
                                      {customerStatus.hasCustomer ? (
                                        <div className="flex items-center gap-1 text-green-700">
                                          <Users className="h-3 w-3" />
                                          <span>Customer ready</span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-1 text-amber-600">
                                          <Users className="h-3 w-3" />
                                          <span>Customer setup needed</span>
                                        </div>
                                      )}
                                      {customerStatus.hasPaymentMethod ? (
                                        <div className="flex items-center gap-1 text-green-700">
                                          <Shield className="h-3 w-3" />
                                          <span>Payment method ready</span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-1 text-amber-600">
                                          <Shield className="h-3 w-3" />
                                          <span>Payment method needed</span>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={checkCustomerStatus}
                                      className="text-xs"
                                    >
                                      Check Setup Status
                                    </Button>
                                  )}
                                  
                                  {/* Setup Button */}
                                  <Button 
                                    className="bg-green-600 hover:bg-green-700 text-white shadow-sm"
                                    onClick={() => handleBulkStudentBilling(studentData.enrollments, 'auto')}
                                  >
                                    <Zap className="h-4 w-4 mr-2" />
                                    {customerStatus?.hasCustomer ? 'Enable Auto-Billing' : 'Setup Auto-Billing'}
                                  </Button>
                                </div>
                              ) : family.billing_preference === 'auto' && !stripeStatus?.isActive ? (
                                <div className="text-center">
                                  <Badge variant="outline" className="text-amber-700 border-amber-300">
                                    Stripe Setup Required
                                  </Badge>
                                </div>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  className="border-blue-300 text-blue-700 hover:bg-blue-50 shadow-sm"
                                  onClick={() => handleBulkStudentBilling(studentData.enrollments, 'manual')}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  Setup Manual Billing
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Class List */}
                          <div className="grid gap-2">
                            {studentData.enrollments.map((enrollment: any, index: number) => (
                              <div key={enrollment.id} className={`flex items-center justify-between py-3 px-4 rounded-lg border ${
                                index % 2 === 0 ? 'bg-white border-gray-100' : 'bg-gray-50 border-gray-100'
                              }`}>
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Calendar className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900">{enrollment.class.name}</div>
                                    <div className="text-xs text-gray-500">Monthly billing</div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-semibold text-gray-900">
                                    ${(enrollment.class.price_cents / 100).toFixed(2)}
                                  </div>
                                  <div className="text-xs text-gray-500">per month</div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Individual Class Controls */}
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <details className="group">
                              <summary className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                                <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                                Setup individual classes
                              </summary>
                              <div className="mt-3 space-y-2">
                                {studentData.enrollments.map((enrollment: any) => (
                                  <div key={enrollment.id} className="flex items-center justify-between py-2 px-3 bg-white rounded border">
                                    <span className="text-sm text-gray-700">{enrollment.class.name}</span>
                                    <div className="flex items-center gap-2">
                                      {family.billing_preference === 'auto' && stripeStatus?.isActive ? (
                                        <Button 
                                          size="sm"
                                          variant="outline"
                                          className="text-green-700 border-green-300 hover:bg-green-50"
                                          onClick={() => setupAutoBillingWithCustomer(enrollment)}
                                        >
                                          <Zap className="h-3 w-3 mr-1" />
                                          Auto
                                        </Button>
                                      ) : (
                                        <Button 
                                          size="sm"
                                          variant="outline"
                                          className="text-blue-700 border-blue-300 hover:bg-blue-50"
                                          onClick={() => handleSetBillingSchedule(enrollment)}
                                        >
                                          <FileText className="h-3 w-3 mr-1" />
                                          Manual
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </details>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Billing Schedules List */}
            <Card>
              <CardHeader>
                <CardTitle>Billing Schedules</CardTitle>
              </CardHeader>
              <CardContent>
                {schedulesLoading ? (
                  <div className="text-center py-8">Loading billing schedules...</div>
                ) : familyBillingSchedules.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No auto-billing schedules found for this family.
                    {stripeStatus?.isActive && (
                      <div className="mt-2 text-sm">
                        Enable auto-billing when creating or editing enrollments.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Billing Day</TableHead>
                          <TableHead>Next Billing</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {familyBillingSchedules.map((schedule) => (
                          <TableRow key={schedule.id}>
                            <TableCell className="font-medium">
                              {schedule.enrollment.student.first_name} {schedule.enrollment.student.last_name}
                            </TableCell>
                            <TableCell>{schedule.enrollment.class.name}</TableCell>
                            <TableCell className="font-medium">
                              ${(schedule.amount_cents / 100).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {schedule.billing_day}{schedule.billing_day === 1 ? 'st' : schedule.billing_day === 2 ? 'nd' : schedule.billing_day === 3 ? 'rd' : 'th'}
                            </TableCell>
                            <TableCell>
                              {format(new Date(schedule.next_billing_date), 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusBadge(schedule.status)}>
                                {schedule.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {schedule.status === 'active' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleScheduleStatusChange(schedule.id, 'paused')}
                                  >
                                    <Pause className="h-3 w-3" />
                                  </Button>
                                )}
                                {schedule.status === 'paused' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleScheduleStatusChange(schedule.id, 'active')}
                                  >
                                    <Play className="h-3 w-3" />
                                  </Button>
                                )}
                                {schedule.status !== 'cancelled' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleScheduleStatusChange(schedule.id, 'cancelled')}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Invoice Creation Dialog */}
      <InvoiceDialog
        open={isAddInvoiceDialogOpen}
        onOpenChange={setIsAddInvoiceDialogOpen}
        families={families}
        onSubmit={handleCreateInvoiceWithRefresh}
        title="Create Invoice"
        initialFamilyId={family.id}
      />
    </div>
  );
};

export default FamilyBillingTab;
